<script>
import keyboardShortcut from '@/filters/keyboard-shortcut';

export default {
  name: 'Toolbar',
  filters: {
    keyboardShortcut
  },
  props: {
    buttons: Array,
    hidden: Boolean,
    position: {
      type: String,
      default: 'none',
      validator(value) {
        return ['none', 'top', 'bottom', 'left', 'right'].includes(value);
      }
    }
  }
};
</script>

<template>
  <div
    ref="toolbar"
    :class="{
      toolbar: true,
      'is-on-top': position === 'top',
      'is-on-bottom': position === 'bottom',
      'is-on-left': position === 'left',
      'is-on-right': position === 'right',
      'is-hidden': hidden
    }"
  >
    <el-button-group v-for="(buttonGroup, groupIndex) in buttons" :key="groupIndex">
      <template v-for="(button, buttonIndex) in buttonGroup">
        <!-- Dropdowns -->
        <el-dropdown v-if="button.menu" :key="buttonIndex" trigger="click">
          <el-button
            :type="button.type"
            :class="{ active: button.active }"
            :title="button.title"
            :disabled="button.disabled"
          >
            <i v-if="button.icon" :class="button.icon" />
            <span v-if="button.iconText" class="icon">{{ button.iconText }}</span>
            <span v-if="button.text">{{ button.text }}</span>
            <i class="el-icon-fa-angle-down el-icon--right" />
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="(menuItem, menuItemIndex) in button.menu"
              :key="menuItemIndex"
              :class="`${menuItem.className || ''} ${menuItem.active ? 'active' : ''}`"
              :disabled="menuItem.disabled"
              :divided="menuItem.divided"
              @click.native="() => (menuItem.click ? menuItem.click() : null)"
            >
              <i v-if="menuItem.icon" :class="`icon ${menuItem.icon}`" />
              <span v-if="menuItem.iconText" class="icon">{{ menuItem.iconText }}</span>
              <span v-if="menuItem.text">{{ menuItem.text }}</span>
              <kbd v-if="menuItem.shortcut">{{ menuItem.shortcut | keyboardShortcut }}</kbd>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <!-- Buttons -->
        <el-button
          v-else
          :key="buttonIndex"
          :type="button.type"
          :class="{ active: button.active }"
          :title="button.title"
          :disabled="button.disabled"
          @click="() => (button.click ? button.click() : null)"
        >
          <i v-if="button.icon" :class="button.icon" />
          <span v-if="button.iconText" class="icon">{{ button.iconText }}</span>
          <span v-if="button.text">{{ button.text }}</span>
        </el-button>
      </template>
    </el-button-group>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

$--toolbar-bg-color: $--color-gray-90;
$--toolbar-border-color: $--color-gray-90;
$--toolbar-button-color: $--color-white;
$--toolbar-button-active-color: $--color-primary;
$--toolbar-button-disabled-color: $--color-gray-70;

.toolbar {
  background-color: $--toolbar-bg-color;
  border-radius: 0.25rem;
  border: solid 1px $--toolbar-border-color;
  box-shadow: 0 1px 0.5rem rgba($--color-black, 0.125);
  transition: 0.2s opacity;

  &.is-hidden,
  &[x-out-of-boundaries] {
    // Added via popper.js when the element is out of the viewport
    opacity: 0;
    pointer-events: none;
  }

  &.is-on-top {
    &::after,
    &::before {
      top: 100%;
      left: 50%;
      border: solid transparent;
      border-color: rgba(135, 135, 135, 0);
      border-top-color: $--toolbar-border-color;
      border-width: 7px;
      margin-left: -7px;
      content: '';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }
  }

  &.is-on-bottom {
    &::before {
      bottom: 100%;
      left: 50%;
      border: solid transparent;
      border-color: rgba(135, 135, 135, 0);
      border-bottom-color: $--toolbar-border-color;
      border-width: 7px;
      margin-left: -7px;
      content: '';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }
  }

  &.is-on-left {
    &::before {
      top: calc(50% - 7px);
      left: calc(100% + 7px);
      border: solid transparent;
      border-color: rgba(255, 255, 255, 0);
      border-left-color: $--toolbar-bg-color;
      border-width: 7px;
      margin-left: -7px;
      content: '';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }
  }

  &.is-on-right {
    &::before {
      top: calc(50% - 7px);
      left: -7px;
      border: solid transparent;
      border-color: rgba(255, 255, 255, 0);
      border-right-color: $--toolbar-bg-color;
      border-width: 7px;
      margin-left: -7px;
      content: '';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }
  }

  > *:not(:first-child) {
    border-left: solid 1px lighten($--toolbar-bg-color, 5%);
  }

  .el-button {
    background-color: $--toolbar-bg-color;
    color: $--toolbar-button-color;
    border: none;
    border-radius: 0;
    padding: 0.65rem;
    transition: 0.1s background-color, 0.1s color;

    i,
    .icon {
      width: 1.2em;
    }

    &:hover {
      background-color: lighten($--toolbar-bg-color, 5%);
      border-radius: 0 !important;
    }

    &.active {
      color: $--toolbar-button-active-color;
    }

    &.is-disabled {
      background-color: $--toolbar-bg-color;
      color: $--toolbar-button-disabled-color;
    }
  }

  > :first-child .el-button:first-child {
    border-top-left-radius: $--button-border-radius;
    border-bottom-left-radius: $--button-border-radius;
  }

  > :last-child .el-button:last-child {
    border-top-right-radius: $--button-border-radius;
    border-bottom-right-radius: $--button-border-radius;
  }
}

.el-dropdown-menu__item {
  &.h1 {
    font-size: 1.2rem;
  }
  &.h2 {
    font-size: 1.1rem;
  }
  &.h3 {
    font-size: 1rem;
  }
  &.h4 {
    font-size: 0.9rem;
  }
  &.h5 {
    font-size: 0.8rem;
  }
  &.h6 {
    font-size: 0.7rem;
  }
  &.small i {
    font-size: 0.675rem;
  }
}
</style>
